import { useQuery } from '@tanstack/react-query';
import supabaseClient from '@/api/supabaseClient.ts';

type BusinessOnboardingRecord = {
  id?: string;
  email?: string;
  photo_url?: string | null;
  bio?: string | null;
  care_types?: string[] | null;
  skills?: string[] | null;
  hourly_rate?: number | null;
  service_area?: string | null;
  availability_dates?: any | null;
  dbs_document_url?: string | null;
  insurance_document_url?: string | null;
  verification_status?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type GetBusinessOnboardingResponse = {
  data: unknown;
};

const useBusinessOnboarding = (email?: string | null, enabled = true) => {
  return useQuery<BusinessOnboardingRecord | null>({
    queryKey: ['business-onboarding', email],
    enabled: enabled && !!email,
    queryFn: async () => {
      if (!email) return null;

      const { data, error } = await supabaseClient.functions.invoke<GetBusinessOnboardingResponse>(
        'get_business_onboarding',
        {
          body: { email },
        }
      );

      if (error) {
        const message = error instanceof Error ? error.message : 'Error while fetching onboarding data.';
        throw new Error(message);
      }

      const response = (data as GetBusinessOnboardingResponse | null)?.data ?? null;
      // Handle possible double-wrapped shape { data: { ...actualData } }
      if (response && typeof response === 'object' && 'data' in (response as Record<string, unknown>)) {
        return (response as Record<string, unknown>)['data'] as BusinessOnboardingRecord;
      }

      return response as BusinessOnboardingRecord | null;
    },
  });
};

export type { BusinessOnboardingRecord };

export default useBusinessOnboarding;
